import { useMemo, useState } from 'react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Button, Dropdown, type ButtonProps } from '@/ui'
import { setPlaidTarget } from '@/reducers/finances/plaidLauncherReducer'
import { selectVisibleFinancialAccounts } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { AddManualAccountModal } from './AddManualAccountModal'
import { SHOW_MANUAL_ACCOUNTS } from './bankAccounts.helpers'
import {
  getConnectedInstitutions,
  type ConnectedInstitution,
} from './connectNewAccountMenu.helpers'
import { useSyncStatusContext } from './SyncStatusContext'

interface ConnectNewAccountMenuProps {
  variant?: ButtonProps['variant']
}

const ExistingConnectionsSection = ({
  institutions,
}: {
  institutions: ConnectedInstitution[]
}) => {
  const dispatch = useAppDispatch()

  return (
    <>
      <Dropdown.Separator />
      <Dropdown.Label className="text-sm text-neutral-500">
        Update Existing Connections
      </Dropdown.Label>
      {institutions.map((institution) => (
        <Dropdown.Item
          key={institution.plaidItemId}
          disabled={institution.hasSyncError}
          onSelect={() =>
            dispatch(
              setPlaidTarget({
                mode: 'manage',
                accountId: institution.accountId,
              })
            )
          }
        >
          {institution.name}
        </Dropdown.Item>
      ))}
    </>
  )
}

export const ConnectNewAccountMenu = ({
  variant = 'secondary',
}: ConnectNewAccountMenuProps) => {
  const dispatch = useAppDispatch()
  const [manualModalOpen, setManualModalOpen] = useState(false)
  const financialAccounts = useReselector(selectVisibleFinancialAccounts)
  const { plaidItems: syncItems } = useSyncStatusContext()

  const institutions = useMemo(
    () => getConnectedInstitutions(financialAccounts, syncItems),
    [financialAccounts, syncItems]
  )

  return (
    <>
      <Dropdown>
        <Dropdown.Trigger asChild>
          <Button variant={variant} iconLeft={faPlus}>
            Manage Connections
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Content align="end">
          <Dropdown.Item
            onSelect={() => dispatch(setPlaidTarget({ mode: 'connect' }))}
          >
            Connect account
          </Dropdown.Item>
          {SHOW_MANUAL_ACCOUNTS && (
            <Dropdown.Item onSelect={() => setManualModalOpen(true)}>
              Add account manually
            </Dropdown.Item>
          )}
          {institutions.length > 0 && (
            <ExistingConnectionsSection institutions={institutions} />
          )}
        </Dropdown.Content>
      </Dropdown>
      {SHOW_MANUAL_ACCOUNTS && manualModalOpen && (
        <AddManualAccountModal onClose={() => setManualModalOpen(false)} />
      )}
    </>
  )
}
